import { useEffect } from 'react';
import { Link } from 'react-router';
import { useAppPath } from '../lib/AppContext';

// Isometric hub on Home: four tiles around the Macro Mate mascot (an egg in the logo's bowl) whose mood and
// speech bubble follow today's protein. Each tile is a real link with a flat label pill.
// A flat block-style stand-in for the illustrated version: every data part (plate rim, week bars,
// the "meal due" dot, the mascot's mood) is its own layer so a redraw can keep them driven by code.
// Geometry is in grid units: x runs down-right on screen, y runs down-left, z is up.
const S = 22; // px per grid unit
const COS = 0.866;
const P = (x, y, z = 0) => [(x - y) * COS * S, (x + y) * 0.5 * S - z * S];
const pts = (...points) => points.map((p) => p.join(',')).join(' ');

const shade = (hex, amount) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift) => Math.round(((n >> shift) & 255) * (1 - amount));
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
};

// Maps (u, v) on the horizontal plane at height z into screen space.
const topPlane = (z) => `matrix(${COS * S} ${0.5 * S} ${-COS * S} ${0.5 * S} 0 ${-z * S})`;
// Maps (u, w) on the vertical plane y = yFix (u along x, w upward) into screen space.
const frontPlane = (yFix) => {
  const [tx, ty] = P(0, yFix, 0);
  return `matrix(${COS * S} ${0.5 * S} 0 ${-S} ${tx} ${ty})`;
};

function Box({ x, y, w, d, h, z = 0, color, face = false, opacity }) {
  const top = z + h;
  const line = { stroke: shade(color, 0.55), strokeWidth: 0.6, strokeLinejoin: 'round' };
  return (
    <g opacity={opacity}>
      <polygon points={pts(P(x + w, y, top), P(x + w, y + d, top), P(x + w, y + d, z), P(x + w, y, z))} fill={shade(color, 0.22)} {...line} />
      <polygon points={pts(P(x, y + d, top), P(x + w, y + d, top), P(x + w, y + d, z), P(x, y + d, z))} fill={shade(color, 0.4)} {...line} />
      <polygon points={pts(P(x, y, top), P(x + w, y, top), P(x + w, y + d, top), P(x, y + d, top))} fill={color} {...line}
        className={face ? 'hub-face' : undefined} />
    </g>
  );
}

// A tight 2×2 diamond: Hôm nay at the back, Thống kê left, Thực đơn right, Ghi lại (a little thicker) in front.
const TILES = {
  today: { color: '#43b07c', box: { x: 0, y: 0, w: 3.9, d: 3.9, h: 0.55 } },
  stats: { color: '#8e72d8', box: { x: 0, y: 4.02, w: 3.9, d: 3.9, h: 0.55 } },
  plan: { color: '#3ba6ab', box: { x: 4.02, y: 0, w: 3.9, d: 3.9, h: 0.55 } },
  log: { color: '#e67b4b', box: { x: 4.02, y: 4.02, w: 3.9, d: 3.9, h: 0.75 } },
};

// Plate whose rim fills to the share of today's calories eaten.
function PlateLayer({ pct }) {
  const z = TILES.today.box.h;
  return (
    <g transform={topPlane(z)}>
      <circle cx={1.95} cy={1.95} r={1.35} fill="#f6f1e7" />
      <circle cx={1.95} cy={1.95} r={1.12} fill="none" stroke="#d9d0bf" strokeWidth={0.16} />
      <circle cx={1.95} cy={1.95} r={1.12} fill="none" stroke="#17804f" strokeWidth={0.16} strokeLinecap="round"
        pathLength={100} strokeDasharray={`${Math.min(100, pct)} 100`} transform="rotate(-90 1.95 1.95)" />
      <circle cx={1.6} cy={1.7} r={0.38} fill="#fff9ea" />
      <circle cx={1.6} cy={1.7} r={0.15} fill="#ecb441" />
      <circle cx={2.35} cy={1.8} r={0.32} fill="#5a9e4b" />
      <circle cx={1.95} cy={2.4} r={0.3} fill="#e8643a" />
    </g>
  );
}

// One bar per weekday along the tile: tall when logged, short when missed, today in yellow, future faint.
function WeekBarsLayer({ week, loaded }) {
  const z = TILES.stats.box.h;
  return (
    <g>
      {week.map((day, i) => {
        const h = day.future || !loaded ? 0.08 : day.logged ? 1.25 : 0.35;
        return (
          <Box key={day.iso} x={0.45 + i * 0.47} y={6.4} w={0.36} d={0.42} h={h} z={z}
            color={day.today ? '#ecb441' : '#e3dbfb'} opacity={day.future || !loaded ? 0.45 : 1} />
        );
      })}
    </g>
  );
}

function BentoLayer() {
  const z = TILES.plan.box.h;
  const top = z + 0.4;
  return (
    <g>
      <Box x={4.62} y={0.6} w={2.6} d={2.6} h={0.4} z={z} color="#fbf6ea" />
      <g transform={topPlane(top)}>
        <rect x={4.82} y={0.8} width={1.25} height={1.15} rx={0.1} fill="#e8643a" />
        <rect x={6.22} y={0.8} width={0.8} height={1.15} rx={0.1} fill="#5a9e4b" />
        <rect x={4.82} y={2.1} width={2.2} height={0.9} rx={0.1} fill="#f3dea0" />
      </g>
    </g>
  );
}

// ---------- Mascot ----------

// Where the mascot stands (its bowl's bottom): to the right of the tiles, lower than the Thực đơn tile, and
// drawn a little larger than the tile props. Its bubble floats well above it, clear of the tiles.
const MASCOT_SCALE = 1.3;
const MASCOT_HEIGHT = 66 * MASCOT_SCALE;
const BUBBLE_GAP = 34;
// The mascot stands right of the diamond, high enough that its bubble (which grows upward when it wraps)
// always clears the highest tile edge.
const TILES_TOP = Math.min(...Object.values(TILES).map(({ box: { x, y, h } }) => P(x, y, h + 0.3)[1]));
const MASCOT_BASE = [P(7.92, 0, 0)[0] + 72, TILES_TOP - 8 + MASCOT_HEIGHT + BUBBLE_GAP];

// Faces for the three moods: hungry (behind on protein), ok (on track), done (goal reached).
function MascotFace({ mood }) {
  const ink = { stroke: '#17201b', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' };
  const blush = (
    <>
      <ellipse cx={-12} cy={-31} rx={3.6} ry={2.1} fill="#f6a3a3" opacity={0.85} />
      <ellipse cx={12} cy={-31} rx={3.6} ry={2.1} fill="#f6a3a3" opacity={0.85} />
    </>
  );
  if (mood === 'hungry') {
    return (
      <>
        <circle cx={-7} cy={-40} r={2.7} fill="#17201b" />
        <circle cx={7} cy={-40} r={2.7} fill="#17201b" />
        <path d="M -7 -29 q 2.3 -2.6 4.7 0 t 4.7 0 t 4.7 0" {...ink} />
        <path className="mascot-sweat" d="M 19 -54 q 3 5 0 7 q -3 -2 0 -7 Z" fill="#6e9cf0" />
        <path d="M -30 -38 q -4 4 0 9 M -35 -38 q -4 4 0 9" stroke="#c9bbf2" strokeWidth={1.8} strokeLinecap="round" fill="none" />
      </>
    );
  }
  if (mood === 'done') {
    const star = (x, y, size) => (
      <path className="mascot-sparkle" fill="#ecb441"
        d={`M ${x} ${y - size} Q ${x + size * 0.2} ${y - size * 0.2} ${x + size} ${y} Q ${x + size * 0.2} ${y + size * 0.2} ${x} ${y + size} Q ${x - size * 0.2} ${y + size * 0.2} ${x - size} ${y} Q ${x - size * 0.2} ${y - size * 0.2} ${x} ${y - size} Z`} />
    );
    return (
      <>
        <path d="M -10 -38 l 3.2 -3.4 l 3.2 3.4 M 3.6 -38 l 3.2 -3.4 l 3.2 3.4" {...ink} />
        {blush}
        <path d="M -7 -31 q 7 8 14 0 Z" fill="#17201b" />
        <ellipse cx={3.2} cy={-27.6} rx={2.6} ry={2.2} fill="#f06a7a" />
        {star(-27, -50, 4)}
        {star(25, -57, 6.5)}
      </>
    );
  }
  return (
    <>
      <path d="M -10.5 -39 q 3.2 -4 6.4 0 M 4.1 -39 q 3.2 -4 6.4 0" {...ink} />
      {blush}
      <path d="M -6.5 -30.5 q 6.5 6 13 0" {...ink} />
    </>
  );
}

const MOOD_LABELS = { hungry: 'đang đói, chưa đủ đạm', ok: 'đang ổn, đúng hướng', done: 'đã đạt mục tiêu đạm' };

function Mascot({ mood }) {
  const [x, y] = MASCOT_BASE;
  return (
    <g transform={`translate(${x} ${y}) scale(${MASCOT_SCALE})`} role="img" aria-label={`Bạn Mate ${MOOD_LABELS[mood]}`}>
      <ellipse cx={0} cy={2} rx={27} ry={6} fill="rgba(0,0,0,0.35)" />
      <g className="mascot-bob">
        <path d="M 0 -66 C 16 -66 22 -42 22 -27 C 22 -14 12 -8 0 -8 C -12 -8 -22 -14 -22 -27 C -22 -42 -16 -66 0 -66 Z"
          fill="#ffffff" stroke="#e6e1d4" strokeWidth={1} />
        <MascotFace mood={mood} />
        <ellipse cx={0} cy={-16} rx={26} ry={6} fill="#d99e00" />
        <path d="M -26 -16 Q -24 0 0 0 Q 24 0 26 -16 Q 0 -9 -26 -16 Z" fill="#f4c026" />
        <path d="M -23 -9 Q 0 -4 23 -9" stroke="#e0a800" strokeWidth={2.5} fill="none" strokeLinecap="round" />
      </g>
    </g>
  );
}

// Camera and dumbbell; the red dot means a meal is due and not logged yet.
function CaptureLayer({ due }) {
  const z = TILES.log.box.h;
  const cam = { x: 5.8, y: 5.0, w: 1.9, d: 1.3, h: 1.05 };
  return (
    <g>
      <Box x={4.5} y={6.2} w={0.28} d={0.95} h={0.62} z={z} color="#2b2f36" />
      <Box x={4.78} y={6.58} w={1.05} d={0.2} h={0.2} z={z + 0.21} color="#8a929e" />
      <Box x={5.83} y={6.2} w={0.28} d={0.95} h={0.62} z={z} color="#2b2f36" />
      <Box x={cam.x + 0.45} y={cam.y - 0.3} w={0.55} d={0.3} h={0.22} z={z + cam.h} color="#2b2f36" />
      <Box {...cam} z={z} color="#2b2f36" />
      <g transform={frontPlane(cam.y + cam.d)}>
        <circle cx={cam.x + cam.w / 2} cy={z + cam.h / 2} r={0.4} fill="#151820" stroke="#cfd6df" strokeWidth={0.07} />
        <circle cx={cam.x + cam.w / 2} cy={z + cam.h / 2} r={0.18} fill="#5b8fd6" />
      </g>
      {due && (
        <g transform={topPlane(z)}>
          <circle className="hub-dot" cx={7.75} cy={6.1} r={0.32} fill="#f04438" stroke="#fff" strokeWidth={0.06} />
        </g>
      )}
    </g>
  );
}

const VIEWBOX = (() => {
  const all = Object.values(TILES).flatMap(({ box: { x, y, w, d, h } }) => [P(x + w, y, h + 0.3), P(x, y + d, 0), P(x + w, y + d, 0), P(x, y, h + 0.3)]);
  all.push([MASCOT_BASE[0] + 36, MASCOT_BASE[1] + 10], [MASCOT_BASE[0] - 36, MASCOT_BASE[1] - MASCOT_HEIGHT - BUBBLE_GAP - 30]);
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const pad = 10;
  return [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) - Math.min(...xs) + pad * 2, Math.max(...ys) - Math.min(...ys) + pad * 2];
})();
// Painter's order: back tile first, the side tiles, then the front one.
const ORDER = ['today', 'stats', 'plan', 'log'];

// The speech bubble over the mascot's head: yellow when the goal is reached.
function MascotBubble({ mascot }) {
  const path = useAppPath();
  const [x, y] = MASCOT_BASE;
  // A full-width row above the mascot: the bubble sits against the right edge (wrapping rather than reaching
  // over the tiles), and the tail is placed under it at the mascot's position.
  const row = { top: `${((y - MASCOT_HEIGHT - BUBBLE_GAP - VIEWBOX[1]) / VIEWBOX[3]) * 100}%`, transform: 'translateY(-100%)' };
  const tailLeft = `${((x - VIEWBOX[0]) / VIEWBOX[2]) * 100}%`;
  const color = mascot.mood === 'done' ? 'bg-[#f4c026] text-[#3b2a00]' : 'bg-white text-[#17201b]';
  const className = `mascot-bubble block max-w-[58%] rounded-[14px] px-3 py-1.5 text-center text-[13px] font-bold leading-snug shadow-md ${color}`;
  const bubble = mascot.onClick ? (
    <button type="button" className={className} aria-label={mascot.spoken} onClick={mascot.onClick}>{mascot.text}</button>
  ) : (
    <Link to={path(mascot.to || '/day')} className={className} aria-label={mascot.spoken}>{mascot.text}</Link>
  );
  return (
    <div className="pointer-events-none absolute inset-x-0 z-10" style={row}>
      <div className="pointer-events-auto flex justify-end">{bubble}</div>
      <span aria-hidden="true" className={`mascot-bubble-tail ${color}`} style={{ left: tailLeft }} />
    </div>
  );
}

// The rise-in plays on the first open of the session only.
let entered = false;

// tiles: { [id]: { to?, onClick?, name, line, spoken } }
// tiles: { [id]: { to, onClick?, spoken } } — each tile is a real link; `spoken` carries its data for screen readers.
// mascot: { mood: 'hungry' | 'ok' | 'done', text, spoken, to?, onClick? } — the bubble is a real button/link.
export default function Hub({ tiles, platePct, week, weekLoaded = true, mealDue, mascot }) {
  const path = useAppPath();
  const animate = !entered;
  useEffect(() => { entered = true; }, []);

  const layers = {
    today: <PlateLayer pct={platePct} />,
    stats: <WeekBarsLayer week={week} loaded={weekLoaded} />,
    plan: <BentoLayer />,
    log: <CaptureLayer due={mealDue} />,
  };
  const linkProps = (id) => {
    const tile = tiles[id];
    return {
      to: path(tile.to),
      'aria-label': tile.spoken,
      onClick: tile.onClick ? (e) => { e.preventDefault(); tile.onClick(); } : undefined,
      role: tile.onClick ? 'button' : undefined,
    };
  };

  return (
    <div className="relative mx-auto w-full max-w-[358px]">
      <svg viewBox={VIEWBOX.join(' ')} className="block w-full overflow-visible" role="group" aria-label="Các mục của Macro Mate">
        <ellipse cx={P(3.96, 3.96)[0]} cy={P(3.96, 3.96)[1] + 6} rx={P(7.92, 0)[0] + 26} ry={P(3.96, 3.96)[1] * 0.62} fill="#0f1d17" />
        {ORDER.map((id, i) => (
          <Link key={id} {...linkProps(id)} className={`hub-tile ${animate ? 'hub-enter' : ''}`} style={{ animationDelay: `${i * 90}ms` }}>
            <g className="hub-press">
              <Box {...TILES[id].box} color={TILES[id].color} face />
              {layers[id]}
            </g>
          </Link>
        ))}
        <Mascot mood={mascot?.mood || 'ok'} />
      </svg>
      {mascot?.text && <MascotBubble mascot={mascot} />}
    </div>
  );
}
